import React from 'react';
import { Box, capitalizeFirstLetter, Inscription, List } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import REGEXP from '@/common/regexp';
import { Dependent } from '@/modules/user/components/ManageProfile/components/Dependents/dependents.types';
import { useStore } from '@/modules/user/components/ManageProfile/components/Dependents/stores/useDependents.store';

import DependentsAge from '../../DependentsAge';

import { EDIT_DISABLED_HINT, isEditDisabled } from './hooks/useDependentsList';

const FIELD_PLACEHOLDER = '-';

interface Props {
  dependent: Dependent;
  isHighlighted?: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
}

const MobileDependentCard: React.FC<Props> = ({ dependent, isHighlighted, onEdit, onDeactivate }) => {
  const { handleSetEditableDependent } = useStore();

  const fullName = `${dependent?.firstName || ''} ${dependent?.lastName || ''}`.trim() || FIELD_PLACEHOLDER;
  const relationship = capitalizeFirstLetter(
    dependent?.relationshipType?.replace(REGEXP.DASH_SYMBOL, ' ') || ''
  );
  const isEditingDisabled = isEditDisabled(dependent?.creationSourceType);

  return (
    <Box
      data-testid={`MobileDependentCard_${dependent?.id}`}
      round="container2Round"
      background="canvas"
      border={{ size: isHighlighted ? '2px' : 'small', color: isHighlighted ? 'accentActive' : 'border2' }}
      pad="spacing16"
      margin={{ bottom: 'spacing8' }}
      direction="row"
      align="center"
      justify="between"
    >
      <Box flex>
        {relationship ? (
          <Box
            alignSelf="start"
            background="border2"
            round="checkboxRound"
            pad={{ horizontal: 'spacing8', vertical: 'spacing2' }}
            margin={{ bottom: 'spacing4' }}
          >
            <Inscription size="12px" weight={500} color="textBody" lineHeight="18px">
              {relationship}
            </Inscription>
          </Box>
        ) : null}

        <Inscription
          data-testId={`MobileDependentCard_${dependent?.id}_name`}
          size="16px"
          weight="bold"
          lineHeight="22px"
          color="textBody"
        >
          {fullName}
        </Inscription>

        <Box direction="row" align="center" margin={{ top: 'spacing4' }}>
          <Inscription size="large" color="textSecondary" lineHeight="22px">
            Date of birth:&nbsp;
          </Inscription>
          <Inscription size="large" color="textBody" lineHeight="22px">
            {dependent?.birthOn ? dayjs(dependent.birthOn).format(DEFAULT_DATE_FORMAT) : FIELD_PLACEHOLDER}
          </Inscription>
          {dependent?.birthOn ? <DependentsAge birthOn={dependent.birthOn} /> : null}
        </Box>
      </Box>

      <Box margin={{ left: 'spacing8' }}>
        <List.Item.Options testId={`dependent_${dependent?.id}`}>
          <List.Item.Options.Item
            data-testId={`MobileDependentCard_${dependent?.id}_edit`}
            {...(isEditingDisabled
              ? { color: 'textDisabled', title: EDIT_DISABLED_HINT, onClick: () => {} }
              : {
                  color: 'textBody',
                  onClick: () => {
                    onEdit();
                    handleSetEditableDependent(dependent);
                  },
                })}
          >
            Edit
          </List.Item.Options.Item>
          <List.Item.Options.Item
            data-testId={`MobileDependentCard_${dependent?.id}_deactivate`}
            onClick={() => {
              onDeactivate();
              handleSetEditableDependent(dependent);
            }}
            isDanger
            color="danger"
          >
            Deactivate
          </List.Item.Options.Item>
        </List.Item.Options>
      </Box>
    </Box>
  );
};

export default MobileDependentCard;
